import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  APP_ROOT,
  IDP_LOGIN_ROUTE,
  MODULE_ROUTES,
  PROFILE_CAPTURE_ROUTES,
  USER_ROUTES,
} from './routes.const';
import { useCallback, useEffect, useRef, useState } from 'react';
import { setAuthToken } from 'common/hooks/auth';
import { useDispatch } from 'react-redux';
import { login, setUser } from 'common/store/slices/auth.slice';
import { useGetUserAppConfigQuery } from 'common/store/api/auth.api';
import { UserModel } from 'common/models/user.model';
import { setAppConfig } from 'common/store/slices/page-config.slice';
import { useResize } from 'common/hooks/resize';
import { Spinner } from 'common/components/loader/Spinner';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { useLoadGridConfigData } from 'common/components/datagrid/hooks/usePersistUserGrid';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import useSignOut from 'common/hooks/useSignOut';
import { PROJECT_ID_PLACEHOLDER, PROJECT_ID_PREFIX } from 'common/constants';
import { useGetWidgetOptions } from 'common/hooks/usePersistWidgetOptions';
import {
  getCaptureDeeplinkTraceId,
  useCaptureDeeplinkTrace,
} from 'common/hooks/useCaptureDeeplinkTrace';
import ErrorPage from 'common/components/errorPages/ErrorPage';

export const ignoreRoutes = [
  IDP_LOGIN_ROUTE,
  MODULE_ROUTES.terms,
  MODULE_ROUTES.cookies,
  MODULE_ROUTES.privacyPolicy,
];

export const nonProjectRoutes = [USER_ROUTES.root];
const CAPTURE_DEEPLINK_BOOTSTRAP_TIMEOUT_MS = 20_000;
export const CAPTURE_DEEPLINK_AUTHORIZED_ROUTE_MOUNT_MARK =
  'kwello.capture-deeplink.authorized-route-mounted';

const AuthorizedRoute = ({ children }: { children: React.ReactNode }) => {
  const [idToken, setIdToken] = useState<string>('');
  const [hasCommittedFreshAppConfig, setHasCommittedFreshAppConfig] = useState(false);
  const [captureDeeplinkBootstrapTimedOut, setCaptureDeeplinkBootstrapTimedOut] = useState(false);
  const { module, page } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, getAccessTokenSilently, user } = useAuth0();
  const { isMobile } = useResize();
  const appConfig = useGetUserAppConfigQuery(idToken, {
    skip: idToken === '',
    refetchOnMountOrArgChange: false,
  });
  const { activeProject, activeModule, captureProjects } = useModuleProjectContext();
  const { compareUrl } = useCompareUrlWithProjectId();
  const traceCaptureDeeplink = useCaptureDeeplinkTrace();
  const { signout, isSigningOut } = useSignOut();
  const isProfileCaptureRoute = compareUrl(PROFILE_CAPTURE_ROUTES.root, { includes: true });
  // The capture-only new-activity route shares the profile-capture path prefix.
  // Treat only the entry URL carrying sourceProfileId as a deeplink attempt so
  // navigation after capture-ready cannot open a second, orphaned trace.
  const isCaptureDeeplink =
    isProfileCaptureRoute && new URLSearchParams(search).has('sourceProfileId');
  const isCaptureDeeplinkRef = useRef(isCaptureDeeplink);
  isCaptureDeeplinkRef.current = isCaptureDeeplink;
  // `/app/profile-capture` is the original Veeva entry point. It reaches this
  // component with module=profile-capture before LegacyAppRoute can translate
  // it to the modern project-scoped route.
  const isProfileCaptureEntry = page === 'profile-capture' || module === 'profile-capture';
  const isCaptureDeeplinkEntryOnMount = useRef(
    isProfileCaptureEntry && new URLSearchParams(search).has('sourceProfileId')
  );

  const { isLoading: isGridConfigLoading, isUninitialized: isGridConfigUninitialized } =
    useLoadGridConfigData();
  const { isLoading: isWidgetOptionsLoading, isUninitialized: isWidgetOptionsUninitialized } =
    useGetWidgetOptions();

  const fetchingOptionalUserData =
    isGridConfigLoading ||
    isGridConfigUninitialized ||
    isWidgetOptionsLoading ||
    isWidgetOptionsUninitialized;
  // Capture deeplinks only require a fresh token and app config. Grid and widget
  // preferences are presentation data and must not hold this route on a spinner.
  const appBootstrapPending =
    idToken === '' ||
    appConfig.isLoading ||
    appConfig.isUninitialized ||
    !hasCommittedFreshAppConfig;

  useEffect(() => {
    if (!isCaptureDeeplinkEntryOnMount.current) return;

    // Keep the route-instance boundary observable in browser performance data.
    // A legacy redirect and its modern destination share one SPA document, so
    // more than one mark proves that AuthorizedRoute remounted during bootstrap.
    performance.mark(CAPTURE_DEEPLINK_AUTHORIZED_ROUTE_MOUNT_MARK);
  }, []);

  useEffect(() => {
    if (!isCaptureDeeplink) return;
    traceCaptureDeeplink('route-entered', {
      hasSourceProfileId: new URLSearchParams(search).has('sourceProfileId'),
    });
  }, [isCaptureDeeplink, search, traceCaptureDeeplink]);

  useEffect(() => {
    if (!isProfileCaptureRoute || !appBootstrapPending) {
      setCaptureDeeplinkBootstrapTimedOut(false);
      return;
    }

    // Convert a stalled auth/config bootstrap into a recoverable support screen;
    // otherwise an upstream request that never settles leaves Capture spinning forever.
    if (isCaptureDeeplink) traceCaptureDeeplink('app-config-loading');
    const timeout = window.setTimeout(() => {
      setCaptureDeeplinkBootstrapTimedOut(true);
      if (isCaptureDeeplinkRef.current) {
        traceCaptureDeeplink('bootstrap-timeout', {
          hasIdToken: idToken !== '',
          appConfigLoading: appConfig.isLoading,
          appConfigUninitialized: appConfig.isUninitialized,
          hasCommittedFreshAppConfig,
        });
      }
    }, CAPTURE_DEEPLINK_BOOTSTRAP_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [
    appBootstrapPending,
    appConfig.isLoading,
    appConfig.isUninitialized,
    hasCommittedFreshAppConfig,
    idToken,
    isCaptureDeeplink,
    isProfileCaptureRoute,
    traceCaptureDeeplink,
  ]);

  const getTokenAndRedirect = useCallback(async () => {
    // If they are an authenticated user, get the access token and set it in the local storage
    if (!isLoading && isAuthenticated) {
      try {
        if (isCaptureDeeplinkRef.current) traceCaptureDeeplink('token-requested');
        const { access_token: accessToken, id_token: idToken } = await getAccessTokenSilently({
          detailedResponse: true,
        });
        if (isCaptureDeeplinkRef.current) traceCaptureDeeplink('token-acquired');
        setAuthToken(accessToken);
        dispatch(login({ accessToken }));
        setHasCommittedFreshAppConfig(false);
        setIdToken(idToken);

        const previousPath = localStorage.getItem('previousKwelloPath');
        const previousSearchValues = localStorage.getItem('previousSearchValues');

        if (isAuthenticated && previousPath) {
          localStorage.removeItem('previousKwelloPath');
          localStorage.removeItem('previousSearchValues');

          navigate(`${previousPath}${previousSearchValues ?? ''}`, {
            replace: true,
          });
        }
      } catch (error) {
        if (isCaptureDeeplinkRef.current) traceCaptureDeeplink('token-failed');
        console.error('Error Refreshing User Token', error);

        // remove local storage items that contain the string auth0spajs
        Object.keys(localStorage).forEach((key) => {
          if (key.includes('auth0spajs')) {
            localStorage.removeItem(key);
          }
        });
        window.location.reload();
      }
    }
  }, [
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    dispatch,
    navigate,
    traceCaptureDeeplink,
  ]);

  useEffect(() => {
    getTokenAndRedirect().catch((err: Error) => console.error(err));
  }, [getTokenAndRedirect]);

  useEffect(() => {
    if (!isLoading && isAuthenticated && appConfig?.data) {
      // add the config to the store
      dispatch(setAppConfig(appConfig.data));
      setHasCommittedFreshAppConfig(true);
      if (isCaptureDeeplink) {
        traceCaptureDeeplink('app-config-ready', {
          captureProjectCount: captureProjects.length,
        });
      }

      // Get user details and merge with Auth0 User and update the store
      const { kwelloUserId, fullName, logo } = appConfig.data;
      if (kwelloUserId) {
        const userDetails: UserModel = {
          ...user,
          id: kwelloUserId,
          kwelloUserId,
          externalName: fullName,
          companyName: logo,
        };
        dispatch(setUser(userDetails));
      }
    }
  }, [
    activeProject,
    appConfig,
    captureProjects.length,
    dispatch,
    isAuthenticated,
    isCaptureDeeplink,
    isLoading,
    isMobile,
    navigate,
    traceCaptureDeeplink,
    user,
  ]);

  useEffect(() => {
    if (!appConfig.isError || isSigningOut) return;

    const errorStatus = (appConfig.error as { status?: number })?.status;
    if (isCaptureDeeplink) {
      traceCaptureDeeplink('app-config-failed', {
        status: errorStatus ?? 'unknown',
      });
    }

    let loginErrorMessage: string;
    switch (errorStatus) {
      case 401:
        loginErrorMessage =
          'Authentication error. Please try logging in again or contact support@accelerationpoint.com.';
        break;
      case 403:
        loginErrorMessage =
          'You do not have permission to access any projects. Please contact support@accelerationpoint.com.';
        break;
      case 404:
        loginErrorMessage =
          'Your account has not been set up yet. Please contact support@accelerationpoint.com.';
        break;
      default:
        loginErrorMessage = 'Login failed. Please refresh the browser and try again.';
        break;
    }

    localStorage.setItem('loginError', loginErrorMessage);
    signout();
  }, [
    appConfig.error,
    appConfig.isError,
    isCaptureDeeplink,
    isSigningOut,
    signout,
    traceCaptureDeeplink,
  ]);

  useEffect(() => {
    if (!isProfileCaptureRoute || !fetchingOptionalUserData) return;
    traceCaptureDeeplink('optional-preferences-bypassed', {
      gridConfigLoading: isGridConfigLoading,
      gridConfigUninitialized: isGridConfigUninitialized,
      widgetOptionsLoading: isWidgetOptionsLoading,
      widgetOptionsUninitialized: isWidgetOptionsUninitialized,
    });
  }, [
    fetchingOptionalUserData,
    isProfileCaptureRoute,
    isGridConfigLoading,
    isGridConfigUninitialized,
    isWidgetOptionsLoading,
    isWidgetOptionsUninitialized,
    traceCaptureDeeplink,
  ]);

  const activeProjectRequired = !nonProjectRoutes.some((route) =>
    compareUrl(route, { includes: true })
  );

  if (captureDeeplinkBootstrapTimedOut) {
    const supportReference = isCaptureDeeplink
      ? ` Support reference: ${getCaptureDeeplinkTraceId()}.`
      : '';

    return (
      <ErrorPage
        titleOverride="Capture is taking longer than expected"
        messageOverride={`Please reload the page. If the issue persists, contact support.${supportReference}`}
      />
    );
  }

  if (appBootstrapPending) {
    return <Spinner />;
  }

  if (!activeModule) {
    return <Navigate to={APP_ROOT} replace={true} />;
  }

  // Make routing decisions only after fresh app config is committed; otherwise
  // captureProjects can be empty or stale during a hard-refresh bootstrap.
  if (isProfileCaptureEntry && !activeProject && captureProjects.length > 0) {
    const firstCaptureProject = captureProjects[0];

    // Veeva links may contain a project that is not in the authenticated user's
    // Capture set. Preserve the external profile query while selecting a valid
    // project. Child routes deliberately restart at the root because their
    // partially built activity belongs to the project being replaced.
    return (
      <Navigate
        to={`${PROFILE_CAPTURE_ROUTES.root.replace(PROJECT_ID_PLACEHOLDER, `${PROJECT_ID_PREFIX}${firstCaptureProject.moduleProjectID}`)}${search}`}
        replace={true}
      />
    );
  }

  if (isProfileCaptureEntry && !activeProject && captureProjects.length === 0) {
    // ProfileCapturePage owns the no-Capture-project permission state. Let it
    // render that guidance instead of redirecting the user into an unrelated
    // Insights project or indexing captureProjects[0].
    return isAuthenticated ? <>{children}</> : <Spinner />;
  }

  if (activeProjectRequired && !activeProject) {
    const firstProject = activeModule?.projects[0];
    const firstRoute = firstProject?.pages[0].Route;

    return (
      <Navigate
        to={firstRoute.replace(
          PROJECT_ID_PLACEHOLDER,
          `${PROJECT_ID_PREFIX}${firstProject.moduleProjectID}`
        )}
        replace={true}
      />
    );
  }

  if (!isProfileCaptureRoute && fetchingOptionalUserData) {
    return <Spinner />;
  }

  // Normal routes retain the legacy preference-loading gate. Capture deliberately
  // renders once its required bootstrap above is complete.
  return isAuthenticated ? <>{children}</> : <Spinner />;
};

export default AuthorizedRoute;
